import { useState, useEffect } from 'react';
import { formatAddress } from '../utils/web3';
import EthereumProvider from '@walletconnect/ethereum-provider';

export const useWallet = () => {
  const [account, setAccount] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [walletType, setWalletType] = useState('');
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    checkConnection();
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
      if (provider?.removeListener) {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('disconnect', handleDisconnect);
      }
    };
  }, [provider]);

  const checkConnection = async () => {
    if (window.ethereum) {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          setIsConnected(true);
          setWalletType('metamask');
        }
      } catch (error) {
        console.error('Error checking connection:', error);
      }
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnect();
    } else {
      setAccount(accounts[0]);
      setIsConnected(true);
    }
  };

  const handleChainChanged = () => {
    window.location.reload();
  };

  const handleDisconnect = () => {
    disconnect();
  };

  const connectWallet = async (type = 'metamask') => {
    setIsConnecting(true);
    setWalletType(type);
    
    try {
      let chosenProvider;

      // --- MetaMask ---
      if (type === 'metamask') {
        if (!window.ethereum) {
          throw new Error('MetaMask is not installed');
        }
        chosenProvider = window.ethereum;
        const accounts = await chosenProvider.request({ method: 'eth_requestAccounts' });
        setAccount(accounts[0]);
        setIsConnected(true);
        setProvider(chosenProvider);
        return accounts[0];
      }

      // --- WalletConnect v2 ---
      else if (type === 'walletconnect') {
        chosenProvider = await EthereumProvider.init({
          projectId: "d1cb0a4454203d21bf88c9ad13676927", // register at https://cloud.walletconnect.com
          chains: [61], // ETC
          showQrModal: true,
        });
        
        chosenProvider.on('accounts_changed', handleAccountsChanged);
        chosenProvider.on('disconnect', handleDisconnect);
        
        await chosenProvider.connect();
        
        const accounts = await chosenProvider.request({ method: 'eth_accounts' });
        setAccount(accounts[0]);
        setIsConnected(true);
        setProvider(chosenProvider);
        return accounts[0];
      }

    } catch (err) {
      console.error('Wallet connection failed:', err);
      throw err;
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnect = async () => {
    try {
      if (provider?.disconnect) {
        await provider.disconnect();
      }
    } catch (err) {
      console.warn('Error disconnecting wallet:', err);
    }
    setAccount('');
    setIsConnected(false);
    setProvider(null);
    setWalletType('');
  };

  return {
    account,
    formattedAddress: formatAddress(account),
    isConnected,
    isConnecting,
    walletType,
    connectWallet,
    disconnect,
  };
};
